import React, { useCallback, useEffect, useState } from 'react';
import { useNotesStore } from '../../stores/notes-store';
import { useAppStore } from '../../stores/appStore';
import { PageHeader } from '../shared-ui/PageHeader';
import { PrimaryButton } from '../shared-ui/PrimaryButton';
import type { NoteBlock } from '@signa/shared';

/**
 * Notes - handoff-style capture tile + card grid. New notes are created via
 * notesStore.createNote({ title, blocks }) - backend stores Tiptap blocks;
 * we synthesize a single paragraph block from the body textarea for now.
 */

export function NotesView() {
  const notes = useNotesStore((s) => s.notes);
  const createNote = useNotesStore((s) => s.createNote);
  const setActiveNoteId = useAppStore((s) => s.setActiveNoteId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Global `N` shortcut opens editor
  useEffect(() => {
    const onOpen = () => setEditorOpen(true);
    window.addEventListener('signa:open-note-editor', onOpen);
    window.addEventListener('signa:new-note', onOpen);
    return () => {
      window.removeEventListener('signa:open-note-editor', onOpen);
      window.removeEventListener('signa:new-note', onOpen);
    };
  }, []);

  const resetForm = () => {
    setTitle('');
    setBody('');
    setEditorOpen(false);
  };

  const save = useCallback(async () => {
    if (saving) return;
    if (!title.trim() && !body.trim()) {
      resetForm();
      return;
    }
    setSaving(true);
    try {
      const blocks: NoteBlock[] = [
        {
          id: `b-${Date.now()}`,
          type: 'paragraph',
          content: body.trim(),
        },
      ];
      await createNote({ title: title.trim() || 'Untitled', blocks });
      resetForm();
    } catch {
      // toast-store handles surfacing
    } finally {
      setSaving(false);
    }
  }, [saving, title, body, createNote]);

  const onBodyKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') resetForm();
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '40px 36px 120px',
          width: '100%',
        }}
      >
        <PageHeader
          title="Notes"
          subtitle={`${notes.length} note${notes.length === 1 ? '' : 's'}`}
          right={
            !editorOpen && (
              <PrimaryButton
                icon={<span>+</span>}
                shortcut="N"
                onClick={() => setEditorOpen(true)}
              >
                New note
              </PrimaryButton>
            )
          }
        />

        {editorOpen && (
          <Editor
            title={title}
            body={body}
            onTitleChange={setTitle}
            onBodyChange={setBody}
            onSave={save}
            onCancel={resetForm}
            saving={saving}
            onBodyKey={onBodyKey}
          />
        )}

        {notes.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {notes.map((n) => (
              <NoteCard
                key={n.id}
                note={n}
                onOpen={() => setActiveNoteId(n.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// New note tile (collapsed state)

function NewNoteTile({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        padding: '18px 22px',
        background: hover ? 'var(--accent-wash)' : 'transparent',
        border: `1.5px dashed ${hover ? 'var(--accent)' : 'var(--rule)'}`,
        borderRadius: 12,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        textAlign: 'left',
        marginBottom: 28,
        transition: 'background 150ms ease, border-color 150ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'var(--accent-wash)',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--accent)',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        +
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>
          Capture a new note
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', marginTop: 2 }}>
          Meeting context, half-formed ideas, quick thoughts - anything.
        </div>
      </div>
    </button>
  );
}

// Inline editor

function Editor({
  title,
  body,
  onTitleChange,
  onBodyChange,
  onSave,
  onCancel,
  saving,
  onBodyKey,
}: {
  title: string;
  body: string;
  onTitleChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  onBodyKey: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--rule)',
        borderRadius: 12,
        padding: '18px 22px',
        marginBottom: 28,
      }}
    >
      <input
        autoFocus
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Title"
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--ink)',
          fontFamily: 'var(--font-display)',
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: '-0.5px',
          marginBottom: 12,
          padding: 0,
        }}
      />
      <textarea
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        onKeyDown={onBodyKey}
        placeholder="Start writing..."
        style={{
          width: '100%',
          minHeight: 160,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--ink)',
          fontFamily: 'var(--font-ui)',
          fontSize: 14.5,
          lineHeight: 1.65,
          resize: 'vertical',
          padding: 0,
        }}
      />
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--rule)',
          paddingTop: 12,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            letterSpacing: '1px',
            color: 'var(--ink-faint)',
          }}
        >
          CMD+ENTER to save / ESC to cancel
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '6px 12px',
              fontSize: 13,
              fontFamily: 'var(--font-ui)',
              color: 'var(--ink-soft)',
              background: 'transparent',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            style={{
              padding: '7px 14px',
              fontSize: 13,
              fontFamily: 'var(--font-ui)',
              fontWeight: 500,
              color: 'var(--on-accent)',
              background: 'var(--accent)',
              border: '1px solid var(--accent)',
              borderRadius: 7,
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save note'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Card

function NoteCard({
  note,
  onOpen,
}: {
  note: { id: string; title: string; snippet: string; updatedAt: string; isPinned: boolean };
  onOpen: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left',
        padding: '20px 22px',
        background: 'var(--card)',
        border: `1px solid ${hover ? 'var(--accent)' : 'var(--rule)'}`,
        borderRadius: 12,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        transition: 'border-color 150ms ease, transform 150ms ease',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          color: 'var(--ink-faint)',
          marginBottom: 8,
        }}
      >
        <span>{formatDate(note.updatedAt)}</span>
        {note.isPinned && (
          <span
            style={{
              color: 'var(--accent)',
              padding: '1px 6px',
              background: 'var(--accent-wash)',
              borderRadius: 3,
            }}
          >
            pinned
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 500,
          letterSpacing: '-0.3px',
          color: 'var(--ink)',
          marginBottom: 6,
        }}
      >
        {note.title || 'Untitled'}
      </div>
      {note.snippet && (
        <div
          style={{
            fontSize: 14,
            color: 'var(--ink-soft)',
            lineHeight: 1.6,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {note.snippet}
        </div>
      )}
    </button>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function EmptyState() {
  return (
    <div
      style={{
        padding: '60px 24px',
        textAlign: 'center',
        color: 'var(--ink-faint)',
        fontSize: 13,
      }}
    >
      No notes yet. Capture your first one above.
    </div>
  );
}
